import React, {useContext, useEffect, useRef, useState} from 'react'
import {useStyles} from "./GeneralEditCourseForm.css";
import FormControl from "@mui/material/FormControl";
import {Checkbox, FormHelperText, TextField} from "@mui/material";
import useGeneralEditCourseForm from "./useGeneralEditCourseForm";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from '@mui/material/Autocomplete';
import {CheckBox, CheckBoxOutlineBlank, Info, Visibility, VisibilityOff} from "@mui/icons-material";
import CourseParticipantCard from "../card/participant/CourseParticipantCard";
import CoursePlaylistCard from "../card/playlist/CoursePlaylistCard";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {useIntl} from "react-intl";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import BasicHint from "../../../../commons/utils/hint/BasicHint";
import hash from "object-hash";
import {Prompt} from 'react-router'
import {DatePicker, DateTimePicker, LocalizationProvider} from "@mui/lab";
import AdapterDateFns from '@mui/lab/AdapterDateFns';

const GeneralEditCourseForm = ({selectedCourse, editedCourseRef, setSaveButtonEnabled}) => {
    const classes = useStyles()

    const dropdownUncheckedIcon = <CheckBoxOutlineBlank fontSize="small"/>;
    const dropdownCheckedIcon = <CheckBox fontSize="small"/>;

    // Internationalization for simple strings
    const intl = useIntl();

    // Get permission-based limited association structure / Checking if the edit/saving operations are allowed
    const {allowedAssociationStructure, isOperationOnObjectAllowed, signedInUserRole} = useContext(FirebaseAuthContext);

    const {
        name,
        handleChangeName,
        fromDate,
        handleChangeFromDate,
        toDate,
        handleChangeToDate,
        association,
        handleChangeAssociation,
        disciplines,
        handleChangeDisciplines,
        participants,
        handleChangeParticipants,
        playlists,
        handleChangePlaylists,
        open,
        handleChangeOpen,
        selectableUsers,
        selectablePlaylists,
        loading
    } = useGeneralEditCourseForm(selectedCourse, editedCourseRef)

    // Handling for hinting the user to save changes in case he tries to leave the form after making changes
    const initialHash = useRef(null)
    const [hasFormChanged, setHasFormChanged] = useState(false)


    useEffect(() => {
        setSaveButtonEnabled(
            isOperationOnObjectAllowed(selectedCourse)
            && name
            && fromDate
            && toDate
            && association
            && disciplines?.length > 0
            && (participants?.length > 0 || open)
            && playlists?.length > 0)

        // Refresh the current hash and set the initial hash at the very beginning
        const currentHash = hash(editedCourseRef.current, {unorderedArrays: true})
        if (!initialHash.current) {
            initialHash.current = currentHash
        }
        setHasFormChanged(initialHash.current !== currentHash)

    }, [name, fromDate, toDate, association, disciplines, participants, playlists, open])

    const showIfTrue = (text, condition) => {
        if (condition) {
            return text
        }
        return ''
    }

    const getClassInBlurMode = (classWithoutBlur, condition) => {
        if (condition) {
            return `${classWithoutBlur} ${classes.blur}`
        } else {
            return classWithoutBlur
        }
    }

    return (
        <fieldset disabled={!isOperationOnObjectAllowed(selectedCourse)} style={{borderWidth: 0}}>

            {/* Security prompt if the user tries to (maybe unintendendly) leave the page.
                This needs to be effective if there are changes made to the current object. */}
            <Prompt
                when={hasFormChanged}
                message={intl.formatMessage({
                    id: 'app.utils.unsavedChanges.hint',
                    defaultMessage: 'Your changes are not saved, are you sure you want to leave?'
                })}
            />

            <div className={classes.column}>

                <div className={classes.row}>

                    {/* Name */}
                    <FormControl className={classes.formControl}>
                        <TextField variant="outlined" required value={name} onChange={handleChangeName}
                                   id="reg-course-name"
                                   label={intl.formatMessage({
                                       id: 'app.courses.edit.form.name.label',
                                       defaultMessage: 'Name'
                                   })}/>

                        <FormHelperText variant="outlined" error={!name} id="reg-course-name-help">
                            {showIfTrue(intl.formatMessage({
                                id: 'app.courses.edit.form.name.help',
                                defaultMessage: 'Enter name'
                            }), !name)}
                        </FormHelperText>
                    </FormControl>

                    {/* From date */}
                    <FormControl className={classes.formControlDate}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker variant="outlined" required value={fromDate} onChange={handleChangeFromDate}
                                        id="reg-course-fromDate"
                                        label={intl.formatMessage({
                                            id: 'app.courses.edit.form.fromDate.label',
                                            defaultMessage: 'From Date'
                                        })}
                                        renderInput={(params) => <TextField {...params} />}/>
                        </LocalizationProvider>

                        <FormHelperText variant="outlined" error={!fromDate} id="reg-course-fromDate-help">
                            {showIfTrue(intl.formatMessage({
                                id: 'app.courses.edit.form.fromDate.help',
                                defaultMessage: 'Enter from date'
                            }), !fromDate)}
                        </FormHelperText>
                    </FormControl>

                    {/* To date */}
                    <FormControl className={classes.formControlDate}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker variant="outlined" required value={toDate} onChange={handleChangeToDate}
                                        id="reg-course-toDate"
                                        label={intl.formatMessage({
                                            id: 'app.courses.edit.form.toDate.label',
                                            defaultMessage: 'To Date'
                                        })}
                                        renderInput={(params) => <TextField {...params} />}/>
                        </LocalizationProvider>

                        <FormHelperText variant="outlined" error={!toDate} id="reg-course-toDate-help">
                            {showIfTrue(intl.formatMessage({
                                id: 'app.courses.edit.form.toDate.help',
                                defaultMessage: 'Enter to date'
                            }), !toDate)}
                        </FormHelperText>
                    </FormControl>

                    {/* Open vs. closed course (only editable for admins) */}
                    <div className={classes.openClosedContainer}>
                        <div className={classes.openClosedDescriptionColumn}>
                            <Checkbox value={open} style={{marginBottom: -10}}
                                      checked={open}
                                      onChange={handleChangeOpen}
                                      checkedIcon={<Visibility style={{height: 40, width: 40}}/>}
                                      icon={<VisibilityOff style={{height: 40, width: 40}}/>}
                                      disabled={signedInUserRole !== 'admin'}/>
                            <div>
                                {open
                                    ? intl.formatMessage({
                                        id: 'app.courses.edit.form.open.label',
                                        defaultMessage: 'Open'
                                    })
                                    : intl.formatMessage({
                                        id: 'app.courses.edit.form.closed.label',
                                        defaultMessage: 'Closed'
                                    })}
                            </div>
                        </div>
                        <div>
                            <BasicHint
                                hintText={
                                    open
                                        ? intl.formatMessage({
                                            id: 'app.courses.edit.form.open.description',
                                            defaultMessage: 'The course is visible for all players with matching disciplines.'
                                        })
                                        : intl.formatMessage({
                                            id: 'app.courses.edit.form.closed.description',
                                            defaultMessage: 'The course is only visible for the selected participants.'
                                        })
                                }
                                options={{trigger: "click", placement: 'right'}}
                                element={<Info/>}
                            />
                        </div>
                    </div>
                </div>

                <div className={classes.row}>

                    {/* Association */}
                    <FormControl className={classes.formControl}>
                        <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                    id="reg-course-association-label">
                            {intl.formatMessage({
                                id: 'app.courses.edit.form.association.label',
                                defaultMessage: 'Association'
                            })}
                        </InputLabel>
                        <Select id="reg-course-association-input" labelId="reg-course-association-label"
                                variant="outlined"
                                aria-describedby="reg-course-association-help"
                                value={association} onChange={handleChangeAssociation}>
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>

                            {Object.keys(allowedAssociationStructure).map((association) =>
                                <MenuItem key={association} value={association}>
                                    {intl.formatMessage({
                                        id: `app.ids.association.${association}`
                                    })}
                                </MenuItem>
                            )}
                        </Select>
                        <FormHelperText variant="outlined" error={!association}
                                        id="reg-course-association-help">
                            {showIfTrue(intl.formatMessage({
                                id: 'app.courses.edit.form.association.help',
                                defaultMessage: 'Enter association'
                            }), !association)}
                        </FormHelperText>
                    </FormControl>


                    {/* Discipline */}
                    <FormControl className={getClassInBlurMode(classes.disciplineContainer, !association)}>
                        <Autocomplete
                            multiple
                            limitTags={2}
                            id="reg-course-discipline-input"
                            aria-describedby="reg-course-discipline-help"
                            options={allowedAssociationStructure[association]?.disciplines.map((d) => d.name) || []}
                            getOptionLabel={(discipline) =>
                                intl.formatMessage({
                                    id: `app.ids.discipline.${discipline}`
                                })}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined"
                                           label={intl.formatMessage({
                                               id: 'app.courses.edit.form.disciplines.label',
                                               defaultMessage: 'Disciplines'
                                           })}
                                           placeholder={intl.formatMessage({
                                               id: 'app.courses.edit.form.disciplines.placeholder',
                                               defaultMessage: 'Add Disciplines'
                                           })}
                                />
                            )}
                            onChange={handleChangeDisciplines}
                            value={disciplines}
                            disabled={!association}

                        />
                        <FormHelperText variant="outlined" error={disciplines.length === 0}
                                        id="reg-course-discipline-help">
                            {showIfTrue(
                                !association
                                    ? intl.formatMessage({
                                        id: 'app.courses.edit.form.disciplines.noAssociationSelected',
                                        defaultMessage: 'Enter association first'
                                    })
                                    : intl.formatMessage({
                                        id: 'app.courses.edit.form.disciplines.help',
                                        defaultMessage: 'Enter disciplines'
                                    }), disciplines.length === 0)}
                        </FormHelperText>
                    </FormControl>
                </div>

                {/* Participants (only show in case of closed - i.e. not open - courses) */}
                {!open && <div className={classes.row}>
                    <FormControl className={getClassInBlurMode(classes.multiSelectContainer, !association)}>
                        <Autocomplete
                            id="course-participants-multi-select"
                            aria-describedby="reg-course-participants-help"
                            onChange={handleChangeParticipants}
                            loading={loading}
                            value={participants}
                            disabled={!association}
                            multiple
                            disableCloseOnSelect
                            options={selectableUsers}
                            isOptionEqualToValue={(option, value) => option.uid === value.uid}
                            getOptionLabel={(user) => user.firstName + ' ' + user.secondName}
                            groupBy={(user) => user.secondName.charAt(0)}
                            renderOption={(props, user) => {
                                return <li {...props}>
                                    <Checkbox
                                        icon={dropdownUncheckedIcon}
                                        checkedIcon={dropdownCheckedIcon}
                                        style={{marginRight: 10}}
                                        checked={participants.some((u) => u.uid === user.uid)}
                                    />
                                    <CourseParticipantCard user={user}/>
                                </li>
                            }}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined"
                                           label={intl.formatMessage({
                                               id: 'app.courses.edit.form.participants.label',
                                               defaultMessage: 'Course Participants'
                                           })}
                                           placeholder={intl.formatMessage({
                                               id: 'app.courses.edit.form.participants.placeholder',
                                               defaultMessage: 'Add Participants'
                                           })}/>
                            )}
                            renderTags={(tValue, getTagProps) => {
                                return tValue.map((user, index) => (
                                    <CourseParticipantCard user={user} {...getTagProps({index})}/>
                                ));
                            }}
                        />

                        <FormHelperText variant="outlined" error={participants.length === 0}
                                        id="reg-course-participants-help">
                            {showIfTrue(
                                !association
                                    ? intl.formatMessage({
                                        id: 'app.courses.edit.form.participants.noAssociationSelected',
                                        defaultMessage: 'Enter association first'
                                    })
                                    : intl.formatMessage({
                                        id: 'app.courses.edit.form.participants.help',
                                        defaultMessage: 'Enter participants'
                                    }), participants.length === 0)}
                        </FormHelperText>
                    </FormControl>

                    {loading && <LoadingProgressIndication size={40}/>}
                </div>}

                {/* Playlists */}
                <div className={classes.row}>
                    <FormControl className={getClassInBlurMode(classes.multiSelectContainer, !association)}>
                        <Autocomplete
                            id="course-playlists-multi-select"
                            aria-describedby="reg-course-playlists-help"
                            onChange={handleChangePlaylists}
                            value={playlists}
                            disabled={!association}
                            multiple
                            disableCloseOnSelect
                            options={selectablePlaylists}
                            getOptionLabel={(playlist) => playlist.name}
                            isOptionEqualToValue={(option, value) => option.key === value.key}
                            groupBy={(playlist) => playlist.name.charAt(0)}
                            renderOption={(props, playlist) => (
                                <li {...props}>
                                    <Checkbox
                                        icon={dropdownUncheckedIcon}
                                        checkedIcon={dropdownCheckedIcon}
                                        style={{marginRight: 10}}
                                        checked={playlists.some((p) => p.key === playlist.key)}
                                    />
                                    <CoursePlaylistCard playlist={playlist}/>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined"
                                           label={intl.formatMessage({
                                               id: 'app.courses.edit.form.playlists.label',
                                               defaultMessage: 'Course Playlists'
                                           })}
                                           placeholder={intl.formatMessage({
                                               id: 'app.courses.edit.form.playlists.placeholder',
                                               defaultMessage: 'Add Playlists'
                                           })}/>
                            )}

                            renderTags={(tValue, getTagProps) => {
                                return tValue.map((playlist, index) => (
                                    <CoursePlaylistCard {...getTagProps({index})} showInfo={true} playlist={playlist}/>
                                ));
                            }}
                        />

                        <FormHelperText variant="outlined" error={playlists.length === 0}
                                        id="reg-course-playlists-help">
                            {showIfTrue(
                                !association
                                    ? intl.formatMessage({
                                        id: 'app.courses.edit.form.playlists.noAssociationSelected',
                                        defaultMessage: 'Enter association first'
                                    })
                                    : intl.formatMessage({
                                        id: 'app.courses.edit.form.playlists.help',
                                        defaultMessage: 'Enter playlists'
                                    }), playlists.length === 0)}
                        </FormHelperText>

                    </FormControl>

                    {loading && <LoadingProgressIndication size={40}/>}
                </div>
            </div>
        </fieldset>
    );
}

export default GeneralEditCourseForm
